/**
 * Config module (PRD §13, §14): load, validate and merge config, then apply the
 * locked compliance values last so no site or remote config can loosen them.
 */

/** Semantic version of the player script (PRD §14). */
export const VERSION = '2.0.18';

/** Google-hosted SDK URLs. Never self-hosted or modified (C3, §3.2 step 3). */
export const IMA_SDK_URL = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
export const GPT_URL = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
export const ADSENSE_URL = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
export const GAM_VAST_BASE = 'https://pubads.g.doubleclick.net/gampad/ads';

/**
 * Compliance values locked in code (PRD §13 "Locked in code").
 * Config can never override these.
 */
export const LOCKED = Object.freeze({
  plcmt: 2, // C1: Accompanying Content
  startMuted: true, // C2, C8
  visibilityThreshold: 0.5, // C9: autoplay only at >= 50% visible
  maxAdDurationMs: 30000, // C18, C23
  minAdPlayerSize: [241, 201], // C17: never at 240x200 or smaller
  minDisplayRefreshSeconds: 30, // §5
  maxDisplayRefreshes: 5, // §5
  maxMobileAdDensity: 0.3, // C21
  maxVideoAdsPerView: 3, // C18
  maxClipsPerView: 3, // §4
  maxContentSecondsPerView: 180, // §4
  minClipSecondsForAd: 40, // C18
  minPrevClipPlayed: 0.8, // §4
  minAdBudgetMs: 15000, // §4
  vastTimeoutMs: 4000, // §3
  vastTimeoutPrebidMs: 5000, // §3
  prebidTimeoutMs: 1000, // §12.3
  gptTimeoutMs: 5000, // §3, §3.1
  imaLoadTimeoutMs: 5000, // §3.1
  cmpTimeoutMs: 2000, // §2.1
  maxPlayersDesktop: 3, // §6
  maxPlayersMobile: 2, // §6
});

/** PRD §13 defaults for window.QS_VIDEO_CONFIG. */
export const DEFAULTS = Object.freeze({
  enabled: true,
  networkCode: '/NETWORK',
  players: [
    // 2.0.8: p1 after paragraph 2 (was 3) for more starts per session. The plugin prints the slot; the script never moves it.
    { id: 'p1', insertAfterParagraph: 2, float: true },
    { id: 'p2', insertAfterParagraph: 12, float: true, minArticleWords: 1500 },
  ],
  articleSelector: '.entry-content, article',
  minParagraphsBetweenPlayers: 8,
  video: {
    adUnit: '/NETWORK/XX_Video',
    sizes: '640x360|640x480',
    vastTimeoutMs: 4000,
    pendingWaitMs: 3000,
    maxAdDurationMs: 30000,
    // 2.0.10: an empty first pre-roll (no ad, not a timeout or error) is asked again up to this many times while
    // the slot is in view, before the display fallback. 0 turns it off; at most 2.
    noFillRetries: 2,
    // 2.0.12: request the first pre-roll when the player is built (within lazyMarginPx, after consent) instead of at
    // 50% visible; it still starts only at 50% visible (C9). An ad unseen for 30 s is dropped and asked again at 50%.
    preRequest: true,
    // 2.0.13: preferred ad media bitrate (kbps). VAST 402 (media timeout) was 53% of fatal video errors on
    // 28 Oct 2026; a smaller rendition starts faster on phone networks.
    bitrateKbps: 1000,
    phoneBitrateKbps: 600,
    // 2.0.14 (noAdFloat): pre-roll attempts from the poster after a no-fill, and the wait between them.
    posterRetries: 6,
    posterRetrySeconds: 30,
    custParams: { site: 'xx' },
  },
  prebid: {
    enabled: false,
    abPercent: 0,
    timeoutMs: 1000,
    adUnitCode: 'qsvp-p1',
    bidders: [],
  },
  adSchedule: {
    prerollPerClip: true,
    autoAdvance: true,
    minClipSecondsForAd: 40,
    maxClipsPerView: 3,
    maxVideoAdsPerView: 3,
    minPrevClipPlayed: 0.8,
    minAdBudgetMs: 15000,
    maxContentSecondsPerView: 180,
    // 2.0.8: when the first pre-roll and all display tiers are empty with the slot in view, play clip 0 muted and
    // try the pre-roll again before the next clip (product decision against C4/COST-5). false = poster + Watch video.
    playOnEmpty: true,
  },
  fallbackDisplay: {
    enabled: true,
    adUnit: '/NETWORK/XX_Video_Fallback',
    sizes: [
      [300, 250],
      [336, 280],
    ],
    refreshSeconds: 30,
    maxRefreshes: 5,
    gptTimeoutMs: 5000,
  },
  // 2.0.4: off unless a site sets its real, approved AdSense IDs (fail-safe against placeholder config).
  fallbackAdsense: { enabled: false }, // set client ('ca-pub-' + 16 digits) and slot to enable
  clips: {
    cdnBase: 'https://vz-xxxx.b-cdn.net/',
    byCategory: { parenting: ['c01', 'c07'] },
    default: ['c02', 'c03'],
  },
  avoidSelectors: ['#top-sticky', '#bottom-sticky', 'header.site-nav', '#cmp-banner'],
  partnerPlayerSelectors: [
    '.cnx-player',
    '[id*="connatix"]',
    '.vidcrunch',
    '[id*="vidcrunch"]',
    '[id^="unibots"]',
    '[class*="vdo_ai"]',
  ],
  // 2.0.9: muted videos and iframes inside these never count as another player (C29). Taboola's thumbnail widgets
  // autoplay muted, looping previews; they blocked autoplay and the float for the whole page view.
  partnerIgnoreSelectors: ['[id^="taboola-"]', '[id^="internal_trc_"]', '.trc_rbox_container'],
  floatDock: ['bottom-right', 'bottom-left', 'top-right'],
  sizeProfiles: {
    desktop: {
      minWidth: 1024,
      player: [640, 360],
      float: [400, 225],
      fallback: [
        [336, 280],
        [300, 250],
      ],
    },
    tablet: {
      minWidth: 768,
      player: 'column-16:9',
      float: [400, 225],
      fallback: [
        [336, 280],
        [300, 250],
      ],
    },
    // 2.0.18: phone ads float at 268x201 (4:3, the smallest size C17 allows); the poster and clips at 300x169 (16:9,
    // like the clips, so no black bars above and below). 300x169 is too short for an ad (C17), so the float grows to
    // 268x201 while an ad plays.
    phone: { minWidth: 360, player: 'full-bleed-16:9', float: [268, 201], clipFloat: [300, 169], fallback: [[300, 250]] },
    narrow: { minWidth: 0, player: null, float: null, fallback: [[300, 250]] },
  },
  minAdPlayerSize: [241, 201],
  mobileFloat: 'replaces-top-sticky',
  // 2.0.11: when the float plus pinned sticky ads would pass 30% (C16), destroy the fewest GPT sticky ads (top anchor
  // first) for the rest of the page view. 'ad': only while a video ad plays; 'always': also for clips; 'off'.
  stickyMakeRoom: 'ad',
  // 2.0.14 (product decision 29 Oct 2026): no display fallback after a video no-fill. The player shows the poster,
  // floats it once the reader passes the slot and keeps asking for the pre-roll. false = display fallback as before.
  noAdFloat: true,
  // 2.0.16: when the floating poster would pass 30% coverage (C16), destroy the Google top anchor slot (gpt_unit_*)
  // for the rest of the page view, never other sticky ads; nothing is removed if that alone is not enough. 'off'.
  posterMakeRoom: 'anchor',
  privacy: { requireCmp: true, cmpTimeoutMs: 2000, childDirected: false },
  traffic: { videoExcludeSources: [], minViewableRate: 0.5 },
  metadata: { cmsid: '0000000' },
  analytics: { mode: 'auto', measurementId: 'G-XXXXXXX', relayUrl: '/qsvp/collect' },
  remoteConfigUrl: '/qsvp/config.json',
  remoteConfigTtlSec: 300,
  waitForInteraction: true,
  // 2.0.8 (default off, IVT risk accepted when on): a trusted scroll also opens the gate. 2.0.1 removed it because
  // browsers fire trusted scroll events on scroll restoration and anchor jumps.
  gateOnScroll: false,
  // 2.0.8 (default off, product decision against C9/C10 when on): a player whose slot is already above the viewport
  // when it is built requests its pre-roll and starts it in the float, without ever playing in the article.
  floatOnArrival: false,
  lazyMarginPx: 300,
  theme: { accent: '#E91E63', logo: '/img/logo-sm.svg' },
  debug: false,
  // Assumption A6 (Q6): the PRD gives no way to mark internal/staging traffic.
  // adTest: true (or ?qsvp_adtest=1) sends adtest=on.
  adTest: false,
  // Assumption A5 (Q5): name of the first-party A/B cookie (value "treatment" or "control").
  abCookieName: 'qsvp_ab',
});

/**
 * True for a plain object (not array, not null).
 * @param {*} value
 * @returns {boolean}
 */
function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Deep-merge config objects. Arrays and scalars from later sources replace earlier ones.
 * @param {...object} sources
 * @returns {object}
 */
export function mergeConfig(...sources) {
  const result = {};
  for (const source of sources) {
    if (!isPlainObject(source)) continue;
    for (const key of Object.keys(source)) {
      const value = source[key];
      if (isPlainObject(value) && isPlainObject(result[key])) {
        result[key] = mergeConfig(result[key], value);
      } else if (isPlainObject(value)) {
        result[key] = mergeConfig(value);
      } else if (Array.isArray(value)) {
        result[key] = value.map((item) => (isPlainObject(item) ? mergeConfig(item) : item));
      } else if (value !== undefined) {
        result[key] = value;
      }
    }
  }
  return result;
}

/**
 * Clamp a number into [min, max], falling back when it is not a finite number.
 * @param {*} value
 * @param {number} min
 * @param {number} max
 * @param {number} fallback
 * @returns {number}
 */
function clampNumber(value, min, max, fallback) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, number));
}

/**
 * Clamp table [section, key, min, max, fallback]: policy tunables may be made stricter, never looser.
 * C18 / §4 caps, §5 refresh, §3 timeouts, §2.1 CMP timeout, §12.3 Prebid.
 */
const CLAMPS = [
  ['adSchedule', 'maxVideoAdsPerView', 0, LOCKED.maxVideoAdsPerView, 3],
  ['adSchedule', 'maxClipsPerView', 0, LOCKED.maxClipsPerView, 3],
  ['adSchedule', 'maxContentSecondsPerView', 0, LOCKED.maxContentSecondsPerView, 180],
  ['adSchedule', 'minClipSecondsForAd', LOCKED.minClipSecondsForAd, 600, 40],
  ['adSchedule', 'minPrevClipPlayed', LOCKED.minPrevClipPlayed, 1, 0.8],
  ['adSchedule', 'minAdBudgetMs', LOCKED.minAdBudgetMs, 30000, 15000],
  ['video', 'vastTimeoutMs', 1000, LOCKED.vastTimeoutMs, 4000],
  ['video', 'pendingWaitMs', 0, 10000, 3000],
  ['video', 'noFillRetries', 0, 2, 2],
  ['video', 'posterRetries', 0, 10, 6],
  ['video', 'posterRetrySeconds', 15, 600, 30],
  ['fallbackDisplay', 'refreshSeconds', LOCKED.minDisplayRefreshSeconds, 3600, 30],
  ['fallbackDisplay', 'maxRefreshes', 0, LOCKED.maxDisplayRefreshes, 5],
  ['fallbackDisplay', 'gptTimeoutMs', 1000, LOCKED.gptTimeoutMs, 5000],
  ['prebid', 'timeoutMs', 100, LOCKED.prebidTimeoutMs, 1000],
  ['prebid', 'abPercent', 0, 100, 0],
  ['privacy', 'cmpTimeoutMs', 0, LOCKED.cmpTimeoutMs, 2000],
];

/**
 * Apply the locked compliance values last (PRD §13, §14 Config module).
 * Tunables that touch policy may only be made stricter, never looser.
 * @param {object} config merged config
 * @returns {object} the same object, locked
 */
export function applyLocked(config) {
  for (const [section, key, min, max, fallback] of CLAMPS) {
    const target = config[section] || (config[section] = {});
    target[key] = clampNumber(target[key], min, max, fallback);
  }
  config.adSchedule.midrolls = false; // C15: locked off
  config.adSchedule.postrolls = false; // §4: locked off
  config.video.maxAdDurationMs = LOCKED.maxAdDurationMs; // C23: locked to 30000
  // C17: the minimum ad player size is locked.
  config.minAdPlayerSize = LOCKED.minAdPlayerSize.slice();
  config.lazyMarginPx = clampNumber(config.lazyMarginPx, 0, 2000, 300);
  if (config.mobileFloat !== 'off') config.mobileFloat = 'replaces-top-sticky';
  config.locked = LOCKED;
  return config;
}

/**
 * Read QA query overrides (?qsvp_debug=1, ?qsvp_prebid=0|1, ?qsvp_adtest=1).
 * @param {string} search location.search
 * @returns {object} partial config
 */
export function queryOverrides(search) {
  const params = new URLSearchParams(search || '');
  const overrides = {};
  if (params.get('qsvp_debug') === '1') overrides.debug = true;
  const prebid = params.get('qsvp_prebid');
  if (prebid === '0' || prebid === '1') overrides.prebidForce = prebid === '1';
  if (params.get('qsvp_adtest') === '1') overrides.adTest = true;
  return overrides;
}

/**
 * Build the final config: defaults < page config < remote config < QA overrides, then locks.
 * @param {object} pageConfig window.QS_VIDEO_CONFIG
 * @param {object|null} remoteConfig fetched remote JSON
 * @param {string} search location.search
 * @returns {object}
 */
export function buildConfig(pageConfig, remoteConfig, search) {
  const merged = mergeConfig(DEFAULTS, pageConfig || {}, remoteConfig || {}, queryOverrides(search));
  // The kill switch wins: a remote enabled:false always disables (§10, §15.2).
  if (remoteConfig && remoteConfig.enabled === false) merged.enabled = false;
  return applyLocked(merged);
}

/**
 * Per-player config: site config with the player's entry merged in (§13 "per player").
 * @param {object} config final config
 * @param {string} playerId
 * @returns {object|null} null when the player id is not configured
 */
export function getPlayerConfig(config, playerId) {
  const entry = (config.players || []).find((player) => player.id === playerId);
  if (!entry) return null;
  return mergeConfig({ float: true }, entry);
}

/**
 * Fetch the remote config (§13: kill switch without a deploy). Caching is the browser's HTTP cache:
 * serve /qsvp/config.json with Cache-Control: max-age=<remoteConfigTtlSec> (300), so a change takes
 * effect within 5 minutes. Failures return null so the page config stays in force.
 * @param {object} options
 * @param {Function} options.fetchFn fetch implementation
 * @param {string} options.url remote config URL
 * @returns {Promise<object|null>}
 */
export async function loadRemoteConfig({ fetchFn, url }) {
  if (!url || typeof fetchFn !== 'function') return null;
  try {
    const response = await fetchFn(url, { credentials: 'same-origin' });
    const remote = response.ok ? await response.json() : null;
    return isPlainObject(remote) ? remote : null;
  } catch (error) {
    return null;
  }
}
